import { useCallback, useMemo, useState } from 'react';
import { CHROMATIC_SHARP } from '@theory/notes';
import { type ChordQuality, buildChord } from '@theory/chords';
import {
  decodeKeyId,
  encodeKeyId,
  getDiatonicChordIds,
  keysContainingChords,
} from '@util/musicTheory';
import { ChordChip } from './ChordChip';
import { KeyChip } from './KeyChip';
import styles from './ByChordPicker.module.css';

const CHORD_QUALITIES: ChordQuality[] = ['maj', 'min', 'dim'];

interface Props {
  onSelectKey: (keyId: string) => void;
}

export function ByChordPicker({ onSelectKey }: Props) {
  const [pickedChordIds, setPickedChordIds] = useState<string[]>([]);

  // 36 chips: 12 roots × 3 qualities, pitch-ordered.
  const allChordChips = useMemo(() => {
    const chips: { chordId: string }[] = [];
    CHROMATIC_SHARP.forEach((root) => {
      CHORD_QUALITIES.forEach((q) => {
        chips.push({ chordId: buildChord(root, q).id });
      });
    });
    return chips;
  }, []);

  // 24 keys: 12 majors then 12 minors.
  const allKeyChips = useMemo(() => {
    const chips: { keyId: string; root: string; quality: 'major' | 'minor' }[] = [];
    CHROMATIC_SHARP.forEach((root) => {
      chips.push({ keyId: encodeKeyId(root, 'major'), root, quality: 'major' });
    });
    CHROMATIC_SHARP.forEach((root) => {
      chips.push({ keyId: encodeKeyId(root, 'minor'), root, quality: 'minor' });
    });
    return chips;
  }, []);

  const survivingKeyIds = useMemo(
    () => new Set(keysContainingChords(pickedChordIds)),
    [pickedChordIds],
  );

  const survivingChordIds = useMemo(() => {
    const set = new Set<string>();
    survivingKeyIds.forEach((keyId) => {
      const d = decodeKeyId(keyId);
      if (!d) return;
      getDiatonicChordIds(d).forEach((id) => set.add(id));
    });
    return set;
  }, [survivingKeyIds]);

  const toggle = useCallback((chordId: string) => {
    setPickedChordIds((prev) =>
      prev.includes(chordId) ? prev.filter((c) => c !== chordId) : [...prev, chordId],
    );
  }, []);

  const noFilter = pickedChordIds.length === 0;
  const survivingChordCount = noFilter ? allChordChips.length : survivingChordIds.size;

  return (
    <div className={styles.wrap}>
      <section className={styles.panel}>
        <header className={styles.panelHeader}>
          <span className={styles.panelLabel}>
            {noFilter ? 'Pick chords you hear' : `Picked: ${pickedChordIds.join(', ')}`}
          </span>
          {!noFilter && (
            <span className={styles.panelCount}>{survivingChordCount} chords stay</span>
          )}
        </header>
        <div className={styles.chordGrid}>
          {allChordChips.map((c) => {
            const selected = pickedChordIds.includes(c.chordId);
            const muted = !selected && !noFilter && !survivingChordIds.has(c.chordId);
            return (
              <div key={c.chordId} className={styles.cell}>
                <ChordChip
                  chordId={c.chordId}
                  state={selected ? 'selected' : muted ? 'muted' : 'default'}
                  onClick={() => toggle(c.chordId)}
                />
              </div>
            );
          })}
        </div>
      </section>

      <section className={styles.panel}>
        <header className={styles.panelHeader}>
          <span className={styles.panelLabel}>{noFilter ? 'All keys' : 'Matching keys'}</span>
          <span className={styles.panelCount}>{survivingKeyIds.size} / 24</span>
        </header>
        <div className={styles.keyGrid}>
          {allKeyChips.map((k) => {
            const muted = !survivingKeyIds.has(k.keyId);
            return (
              <div key={k.keyId} className={styles.cell}>
                <KeyChip
                  rootNote={k.root}
                  quality={k.quality}
                  state={muted ? 'muted' : 'default'}
                  onClick={() => onSelectKey(k.keyId)}
                />
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
